import React, { Suspense, lazy } from 'react';
import RouteAccessGuard from 'access/RouteAccessGuard';

import { MainLayout } from 'components/Layout/MainLayout';
import PostLoginOrDefaultRedirect from 'features/auth/components/PostLoginOrDefaultRedirect/PostLoginOrDefaultRedirect';
import AccessibleLandingRedirect from 'features/auth/components/AccessibleLandingRedirect/AccessibleLandingRedirect';
import {
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Loader } from '@urbantxt/loader';
import UserSessionProvider from 'providers/UserSessionProvider';

const RosieRoutes = lazy(() => import('features/rosie/routes'));
const BusinessCardPage = lazy(
  () => import('features/labs/routes/BusinessCardPage'),
);

const TxterRoutes = lazy(() => import('features/txters/routes'));
const SclaRoutes = lazy(() => import('features/scla/routes'));
const SclaReaderRoutes = lazy(
  () => import('features/scla/routes/SclaReaderRoutes'),
);
const HackathonRoutes = lazy(() => import('features/hackathon/routes'));
const SealsLegacyAppRoutes = lazy(
  () => import('features/seals-legacy/routes'),
);
const SealsAppRoutes = lazy(() => import('features/seals/routes'));
const EmailRoutes = lazy(() => import('features/email/routes'));
const LabsRoutes = lazy(() => import('features/labs/routes'));
const AutomatedCallsRoutes = lazy(
  () => import('features/automated-calls/routes'),
);
const BitwardenRoutes = lazy(() => import('features/bitwarden/routes'));
const DropboxAuditRoutes = lazy(
  () => import('features/dropbox-audit/routes'),
);
const ArcadeGamesRoutes = lazy(
  () => import('features/arcade-games/routes'),
);
const MothershipAdminRoutes = lazy(
  () => import('features/mothership-admin/routes'),
);
const BatchSmsRoutes = lazy(() => import('features/batch-sms/routes'));
const TxtMerchRoutes = lazy(() => import('features/txt-merch/routes'));
const TxtProgramsRoutes = lazy(
  () => import('features/txt-programs/routes'),
);
const EquipmentRoutes = lazy(() => import('features/equipment/routes'));
const BagelConfigPage = lazy(
  () => import('features/bagel/config/routes/BagelConfigPage'),
);
const BagelDocumentViewerPage = lazy(
  () => import('features/bagel/routes/BagelDocumentViewerPage'),
);
const SignedFormsRoutes = lazy(
  () => import('features/signed-forms/routes'),
);
const TxtLearningRoutes = lazy(
  () => import('features/txt-learning/routes'),
);
const DashboardPage = lazy(
  () => import('features/dashboard/DashboardPage'),
);
const SurveysRoutes = lazy(() => import('features/surveys/routes'));
const LabelPrintingRoutes = lazy(
  () => import('features/label-printing/routes'),
);

/**
 * Renders the routes. A loader is shown if user data is being fetched.
 * A loader is shown for routes that are lazy loaded
 * @returns
 */
function LegacyEquipmentDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/technology/equipment/${id}`} replace />;
}

/** Old `/txters/*` URLs → `/my-txt/profiles` or `/my-txt/profile/...` (query/hash preserved). */
function LegacyTxtersRedirect() {
  const { pathname, search, hash } = useLocation();
  let pathnameTarget = '/my-txt/profiles';
  if (pathname.startsWith('/txters/details/')) {
    pathnameTarget = pathname.replace(
      '/txters/details/',
      '/my-txt/profile/',
    );
  }
  return (
    <Navigate to={{ pathname: pathnameTarget, search, hash }} replace />
  );
}

function App() {
  return (
    <UserSessionProvider>
      {({ user }) => (
        <MainLayout>
          <Suspense
            fallback={
              <Loader theme="light" isVisible fullscreen={false} />
            }
          >
            <Outlet context={{ user }} />
          </Suspense>
        </MainLayout>
      )}
    </UserSessionProvider>
  );
}

export const protectedRoutes = [
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: 'txters/*',
        element: <LegacyTxtersRedirect />,
      },
      {
        path: 'my-txt/*',
        element: <TxterRoutes />,
      },
      {
        path: 'scla/mobile-applications/*',
        element: <MothershipAdminRoutes />,
      },
      {
        path: 'scla/*',
        element: <SclaRoutes />,
      },
      {
        path: 'hackathon/*',
        element: <HackathonRoutes />,
      },
      {
        path: 'seals/legacy/*',
        element: <SealsLegacyAppRoutes />,
      },
      {
        path: 'seals/*',
        element: <SealsAppRoutes />,
      },
      {
        path: 'labs/*',
        element: <LabsRoutes />,
      },
      {
        path: 'automated-calls/*',
        element: <AutomatedCallsRoutes />,
      },
      {
        path: 'batch-sms/*',
        element: <BatchSmsRoutes />,
      },
      {
        path: 'txt-merch/*',
        element: <TxtMerchRoutes />,
      },
      {
        path: 'txt-programs/*',
        element: <TxtProgramsRoutes />,
      },
      {
        path: 'equipment/stats',
        element: <Navigate to="/technology/equipment" replace />,
      },
      {
        path: 'equipment/:id',
        element: <LegacyEquipmentDetailRedirect />,
      },
      {
        path: 'equipment',
        element: <Navigate to="/technology/equipment" replace />,
      },
      {
        path: 'technology/*',
        element: <EquipmentRoutes />,
      },
      {
        path: 'bagel/documents/:id',
        element: (
          <RouteAccessGuard ruleId="bagel-config">
            <Suspense
              fallback={
                <Loader theme="light" isVisible fullscreen={false} />
              }
            >
              <BagelDocumentViewerPage />
            </Suspense>
          </RouteAccessGuard>
        ),
      },
      {
        path: 'bagel/config',
        element: (
          <RouteAccessGuard ruleId="bagel-config">
            <Suspense
              fallback={
                <Loader theme="light" isVisible fullscreen={false} />
              }
            >
              <BagelConfigPage />
            </Suspense>
          </RouteAccessGuard>
        ),
      },
      {
        path: 'signed-forms/*',
        element: <SignedFormsRoutes />,
      },
      {
        path: 'txt-learning/*',
        element: <TxtLearningRoutes />,
      },
      {
        path: 'surveys/*',
        element: <SurveysRoutes />,
      },
      {
        path: 'label-printing/*',
        element: <LabelPrintingRoutes />,
      },
      {
        path: 'emails/*',
        element: <EmailRoutes />,
      },
      {
        path: 'admin/dropbox/audit/*',
        element: <DropboxAuditRoutes />,
      },
      {
        path: 'admin/bitwarden/*',
        element: <BitwardenRoutes />,
      },
      {
        path: 'admin/arcade-games/*',
        element: <ArcadeGamesRoutes />,
      },
      {
        path: 'admin/mothership/*',
        element: <Navigate to="/scla/mobile-applications" replace />,
      },
      {
        path: 'admin/*',
        element: <RosieRoutes />,
      },
      // { path: 'txt-labs/*', element: <TxtLabsRoutes /> },
      // { path: 'emails/*', element: <EmailRoutes /> },
      // { path: 'hackathon/*', element: <HackathonRoutes /> },
      // { path: 'scla/*', element: <SclaRoutes /> },
      // { path: 'hustlencode/*', element: <HustleNCodeRoutes /> },
      // { path: 'office/*', element: <AttendanceLogsRoutes /> },
      // { path: '', element: <Navigate to="/app/hackathon/volunteers" /> },
      // { path: '*', element: <Navigate to="/app/hackathon/volunteers" /> },
      {
        path: 'dashboard',
        element: (
          <Suspense
            fallback={
              <Loader theme="light" isVisible fullscreen={false} />
            }
          >
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: '',
        element: (
          <PostLoginOrDefaultRedirect defaultPath="/my-txt/profiles" />
        ),
      },
    ],
  },
  {
    path: '/business-cards',
    element: (
      <RouteAccessGuard ruleId="labs-business-cards">
        <Suspense fallback={<Loader theme="dark" isVisible fullscreen />}>
          <BusinessCardPage />
        </Suspense>
      </RouteAccessGuard>
    ),
  },
  {
    path: '*',
    element: <AccessibleLandingRedirect />,
  },
];

export const sclaReaderProtectedRoutes = [
  {
    path: '/',
    element: <App />,
    children: [
      { path: 'scla/*', element: <SclaReaderRoutes /> },
      {
        path: '',
        element: <PostLoginOrDefaultRedirect defaultPath="/scla" />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/scla" />,
  },
];
